/**
 * PublicFooter — Shared footer for all public-facing pages
 * (Landing, Pricing, Privacy, Terms, etc.).
 *
 * Shows logo, copyright, OpenClaw attribution, and legal links.
 * All links are tracked via analytics helpers.
 */

import { Box, Container, Text, Stack, Group } from '@mantine/core';
import { Link } from 'react-router-dom';
import { HaiveLogo } from './HaiveLogo';
import { trackLinkClick } from '../../analytics';

export function PublicFooter() {
  return (
    <Box
      component="footer"
      py={40}
      style={{
        borderTop: '1px solid rgba(255,255,255,.05)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container size="lg">
        <Stack align="center" gap="xs">
          <HaiveLogo h={18} variant="dimmed" />
          <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>
            © {new Date().getFullYear()} Haive. All rights reserved.
          </Text>
          <Text size="xs" c="dimmed" ta="center" style={{ opacity: 0.5 }}>
            Proudly built on{' '}
            <Text
              component="a"
              href="https://openclaw.ai"
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              c="dimmed"
              td="none"
              style={{ opacity: 1, borderBottom: '1px solid rgba(255,255,255,.15)' }}
              onClick={() => trackLinkClick('OpenClaw', 'https://openclaw.ai', true)}
            >
              OpenClaw
            </Text>
            {' '}— the open-source AI gateway making this possible.
          </Text>
          <Group gap="sm" mt={4}>
            <FooterLink to="/terms">Terms</FooterLink>
            <Text size="xs" c="dimmed" style={{ opacity: 0.4 }}>·</Text>
            <FooterLink to="/privacy">Privacy</FooterLink>
          </Group>
        </Stack>
      </Container>
    </Box>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Text
      component={Link}
      to={to}
      size="xs"
      c="dimmed"
      td="none"
      style={{
        opacity: 0.55,
        borderBottom: '1px solid transparent',
        transition: 'opacity .2s',
      }}
      onClick={() => trackLinkClick(String(children), to, false)}
      onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.currentTarget.style.opacity = '0.8';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.currentTarget.style.opacity = '0.55';
      }}
    >
      {children}
    </Text>
  );
}
